import {
  Button,
  Page,
  Card,
  FlatActionList,
  Stack,
  Divider,
} from "@towersystems/roam-common-native";
import { useTranslation } from "../../../../utilities/i18n";
import { Colors, Switch, View } from "react-native-ui-lib";
import {
  CheckoutRootParamList,
  CheckoutCreateSaleQuoteRootScreenParamList,
} from "../../../../types";
import { StackScreenProps } from "@react-navigation/stack";
import { useCreateSaleQuoteRootScreenContextProvider } from "../CreateSaleQuoteRootScreen/hooks";
import React from "react";
import { CustomerCard } from "../../components/CustomerCard";
import { SaleSaleItemsSummary } from "../../components/SaleSaleItemsSummary";
import { SaleTotalsSummary } from "../../components/SaleTotalsSummary";
import { SalePaymentSummary } from "../../components/SalePaymentSummary";
import { SaleState } from "../../../../utilities/data/model/Sale";

export type CompleteQuoteScreenProps = StackScreenProps<
  CheckoutCreateSaleQuoteRootScreenParamList & CheckoutRootParamList,
  "CompleteQuoteScreen"
>;

export const CompleteQuoteScreen = ({
  navigation,
}: CompleteQuoteScreenProps) => {
  const screenContext = useCreateSaleQuoteRootScreenContextProvider();
  const [submitting, setSubmitting] = React.useState(false);
  const { t } = useTranslation("quote");
  const { sale } = screenContext;

  const handleOnQuoteAcceptedChange = React.useCallback(
    async (value: boolean) => {
      return sale.setQuoteAcccepted(value).catch((e) => console.log(e));
    },
    []
  );

  const handleOnCompleteQuote = React.useCallback(async () => {
    try {
      setSubmitting(true);
      if (sale.isClosed) {
        navigation.reset({
          index: 0,
          routes: [{ name: "CheckoutSummaryRootScreen" }],
        });
        return;
      }
      if (sale.quoteAccepted == false || !sale.quoteAccepted) {
        await sale.transitionToState(SaleState.QUOTE_OPEN);
        navigation.reset({
          index: 0,
          routes: [{ name: "CheckoutSummaryRootScreen" }],
        });
      } else {
        navigation.navigate("AcceptedQuoteDepositRootScreen", {
          screen: "AcceptedQuoteDepositScreen",
        });
      }
    } catch (err) {
      console.log(err);
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
  }, [sale]);

  // @ts-ignore
  const customerCardMarkup = <CustomerCard customerId={sale.customer.id} />;

  const acceptedQuoteCardMarkup = (
    <Card>
      <FlatActionList
        items={[
          {
            key: "quote-accepted",
            title: t("form.quote_accepted.label"),
            subTitle: t("form.quote_accepted.description"),
            componentRight: () => {
              return (
                <View>
                  <Switch
                    value={sale.quoteAccepted}
                    onValueChange={handleOnQuoteAcceptedChange}
                  />
                </View>
              );
            },
          },
        ]}
      />
    </Card>
  );

  const itemsMarkup = (
    <Card title={t("common:items")}>
      <SaleSaleItemsSummary sale={sale} />
    </Card>
  );

  const paymentsMarkup =
    sale.payments.length >= 1 ? (
      <Card title={t("common:payments")}>
        <SalePaymentSummary sale={sale} />
      </Card>
    ) : undefined;

  const totalsMarkup = (
    <Card title={t("common:totals")}>
      <SaleTotalsSummary sale={sale} />
    </Card>
  );

  const footerMarkup = (
    <Button
      disabled={submitting}
      label={t("button.complete_quote")}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleOnCompleteQuote}
    ></Button>
  );

  return (
    <Page
      title={t("create_quote_title")}
      secondaryTitle={t("create_quote_subtitle")}
      footer={footerMarkup}
    >
      <Stack vertical>
        {customerCardMarkup}
        <Divider />
        {acceptedQuoteCardMarkup}
        <Divider />
        {itemsMarkup}
        {paymentsMarkup}
        {totalsMarkup}
      </Stack>
    </Page>
  );
};
